import { Controller } from "@hotwired/stimulus"
import AdvisorSessionManager from "../modules/salary_lab/advisor_session_manager"
import {
  renderMessage,
  renderGoalButtons,
  renderSummary,
  showTypingIndicator,
  hideTypingIndicator,
  createStreamingBubble,
  appendStreamDelta,
  finalizeStreamBubble,
  insertToolCard,
  updateTypingText
} from "../modules/salary_lab/advisor_renderer"
import { renderToolCard } from "../modules/salary_lab/advisor_cards"
import { trackAdvisorSessionStart, trackAdvisorFirstMessage } from "../modules/salary_lab/ga4_tracker"

export default class extends Controller {
  static targets = [
    "messages",
    "input",
    "sendButton",
    "goals",
    "typingIndicator",
    "typingText",
    "messageCount",
    "summary",
    "sessionInfo",
    "inputArea",
    "personaPicker",
    "preChat",
    "sessionCards",
    "backLink"
  ]

  static values = {
    entryContext: Object,
    createUrl: String,
    messageUrl: String,
    streamUrl: String,
    sessionType: { type: String, default: "career_checkin" },
    resumeSessionId: String,
    resumeUrl: String
  }

  connect() {
    this.manager = new AdvisorSessionManager()
    this._firstMessageTracked = false
    this.selectedPersona = "coach"
    this._resizeHandler = () => this.adjustChatHeight()
    window.addEventListener("resize", this._resizeHandler)
    this.detectSessionParams()

    // Gate states render only the preChat pitch with no chat shell; there is
    // nothing to resume or start, so stay idle instead of grabbing targets
    // that were never rendered.
    if (!this.hasMessagesTarget) return

    if (this.hasResumeSessionIdValue && this.resumeSessionIdValue) {
      this.resumeSession()
    } else if (this._isNewSessionLanding) {
      // Landing page: let user pick persona and session type before starting
    } else {
      this.hidePersonaPicker()
      this.startSession()
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._resizeHandler)
  }

  async startSession() {
    this.disableInput()
    showTypingIndicator(this.typingIndicatorTarget)

    try {
      const data = await this.manager.startSession(
        this.createUrlValue, this.entryContextValue, this.sessionTypeValue, this.selectedPersona
      )

      hideTypingIndicator(this.typingIndicatorTarget)

      if (data.error) {
        renderMessage(this.messagesTarget, "system", data.error)
        return
      }

      renderMessage(this.messagesTarget, "assistant", data.html)
      this.cleanUrl()
      this.updateCounters()
      this.scrollToBottom()
      this.enterImmersiveMode()
      trackAdvisorSessionStart(this.sessionTypeValue)

      if (data.goals) {
        renderGoalButtons(this.goalsTarget, data.goals, (goal) => this.selectGoal(goal))
        this.goalsTarget.classList.remove("hidden")
      }
    } catch (error) {
      hideTypingIndicator(this.typingIndicatorTarget)
      renderMessage(this.messagesTarget, "system", "Unable to start session. Please refresh and try again.")
    } finally {
      this.enableInput()
    }
  }

  async resumeSession() {
    this.disableInput()
    showTypingIndicator(this.typingIndicatorTarget)

    try {
      const response = await fetch(this.resumeUrlValue, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        hideTypingIndicator(this.typingIndicatorTarget)
        this.startSession()
        return
      }

      const data = await response.json()
      hideTypingIndicator(this.typingIndicatorTarget)

      this.manager.resume(data.session_id, data)

      for (const msg of data.messages) {
        renderMessage(this.messagesTarget, msg.role, msg.html)
      }

      this.hidePersonaPicker()
      this.updateCounters()
      this.scrollToBottom()
      this.enterImmersiveMode()
    } catch {
      hideTypingIndicator(this.typingIndicatorTarget)
      this.startSession()
    } finally {
      this.enableInput()
    }
  }

  async sendMessage(event) {
    event.preventDefault()

    const message = this.inputTarget.value.trim()
    if (!message || this.manager.sending) return

    if (!this.manager.sessionId) {
      await this.startSessionThenSend(message)
      return
    }

    if (!this._firstMessageTracked) {
      this._firstMessageTracked = true
      trackAdvisorFirstMessage(this.sessionTypeValue)
    }

    renderMessage(this.messagesTarget, "user", message)
    this.inputTarget.value = ""
    this.autoResize()
    this.goalsTarget.classList.add("hidden")
    this.disableInput()
    this.setTypingText("Thinking...")
    showTypingIndicator(this.typingIndicatorTarget)
    this.scrollToBottom()

    // Use streaming for analysis stage when stream URL is available
    if (this.shouldStream()) {
      return this.sendMessageStreaming(message)
    }

    try {
      const data = await this.manager.sendMessage(this.messageUrlValue, message)
      hideTypingIndicator(this.typingIndicatorTarget)

      if (!data) return

      if (data.html) {
        renderMessage(this.messagesTarget, "assistant", data.html)
      }
      this.handleResponseData(data)
    } catch (error) {
      hideTypingIndicator(this.typingIndicatorTarget)
      renderMessage(this.messagesTarget, "system", "Unable to send message. Please try again.")
    } finally {
      this.enableInput()
    }
  }

  async sendMessageStreaming(message) {
    let streamBubble = null

    try {
      await this.manager.sendMessageStream(
        this.streamUrlValue,
        this.messageUrlValue,
        message,
        {
          onStatus: (data) => {
            this.setTypingText(data.message)
          },

          onToolResults: (data) => {
            if (!data.tools) return

            // Only render cards for tools that have a visual representation
            const cards = data.tools
              .map((tool) => renderToolCard(tool.name, tool.result))
              .filter(Boolean)

            if (cards.length === 0) return

            // Create streaming bubble if not yet created (needed as anchor for card insertion)
            if (!streamBubble) {
              hideTypingIndicator(this.typingIndicatorTarget)
              streamBubble = createStreamingBubble(this.messagesTarget)
            }

            for (const card of cards) {
              insertToolCard(this.messagesTarget, streamBubble, card)
            }
            this.scrollToBottom()
          },

          onTextDelta: (data) => {
            if (!streamBubble) {
              hideTypingIndicator(this.typingIndicatorTarget)
              streamBubble = createStreamingBubble(this.messagesTarget)
            }
            appendStreamDelta(streamBubble, data.text)
            this.scrollToBottom()
          },

          onStreamEnd: (data) => {
            hideTypingIndicator(this.typingIndicatorTarget)

            if (streamBubble && data.html) {
              finalizeStreamBubble(streamBubble, data.html)
            } else if (streamBubble && !data.html) {
              // Remove empty streaming bubble (e.g., tool calls with no follow-up text)
              streamBubble.parentElement?.remove()
              streamBubble = null
            } else if (data.html) {
              renderMessage(this.messagesTarget, "assistant", data.html)
            }

            this.handleResponseData(data)
          },

          onBlocked: (data) => {
            hideTypingIndicator(this.typingIndicatorTarget)
            renderMessage(this.messagesTarget, "system", data.message)
            if (data.locked || data.rate_limited || data.cost_cap) {
              this.inputAreaTarget.classList.add("hidden")
            }
            this.updateCounters()
            this.scrollToBottom()
          },

          onError: (data) => {
            hideTypingIndicator(this.typingIndicatorTarget)
            // Clean up empty streaming bubble before showing error
            if (streamBubble) {
              streamBubble.parentElement?.remove()
              streamBubble = null
            }
            renderMessage(this.messagesTarget, "system", data.message || "Something went wrong.")
            this.scrollToBottom()
          }
        }
      )
    } catch (error) {
      hideTypingIndicator(this.typingIndicatorTarget)
      renderMessage(this.messagesTarget, "system", "Unable to send message. Please try again.")
    } finally {
      this.enableInput()
    }
  }

  async startSessionThenSend(message) {
    this.hidePersonaPicker()
    this.disableInput()
    showTypingIndicator(this.typingIndicatorTarget)

    try {
      const data = await this.manager.startSession(
        this.createUrlValue, this.entryContextValue, this.sessionTypeValue, this.selectedPersona
      )
      hideTypingIndicator(this.typingIndicatorTarget)

      if (data.error) {
        renderMessage(this.messagesTarget, "system", data.error)
        this.enableInput()
        return
      }

      renderMessage(this.messagesTarget, "assistant", data.html)
      this.cleanUrl()
      this.updateCounters()
      this.scrollToBottom()
      this.enterImmersiveMode()
    } catch (error) {
      hideTypingIndicator(this.typingIndicatorTarget)
      renderMessage(this.messagesTarget, "system", "Unable to start session. Please refresh and try again.")
      this.enableInput()
      return
    }

    this.enableInput()
    this.inputTarget.value = message
    this.element.querySelector("form").requestSubmit()
  }

  selectGoal(goal) {
    this.inputTarget.value = goal.label
    this.goalsTarget.classList.add("hidden")
    this.element.querySelector("form").requestSubmit()
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.element.querySelector("form").requestSubmit()
    }
  }

  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px"
    this.adjustChatHeight()
  }

  selectPersona(event) {
    const btn = event.currentTarget
    if (btn.disabled) return

    this.selectedPersona = btn.dataset.persona
    this.highlightPersonaButton(this.selectedPersona)
  }

  navigateSessionType(event) {
    event.preventDefault()
    const url = new URL(event.currentTarget.href)
    if (this.selectedPersona && this.selectedPersona !== "coach") {
      url.searchParams.set("persona", this.selectedPersona)
    }
    window.location.href = url.toString()
  }

  enterImmersiveMode() {
    document.body.classList.add("advisor-immersive")

    if (this.hasPreChatTarget) this.preChatTarget.classList.add("hidden")
    if (this.hasSessionCardsTarget) this.sessionCardsTarget.classList.add("hidden")
    if (this.hasBackLinkTarget) this.backLinkTarget.classList.remove("hidden")

    this.adjustChatHeight()
    this.scrollToBottom()
  }

  exitImmersiveMode(event) {
    if (event) event.preventDefault()

    document.body.classList.remove("advisor-immersive")

    if (this.hasPreChatTarget) this.preChatTarget.classList.remove("hidden")
    if (this.hasSessionCardsTarget) this.sessionCardsTarget.classList.remove("hidden")
    if (this.hasBackLinkTarget) this.backLinkTarget.classList.add("hidden")

    const chatCard = this.messagesTarget.closest(".advisor-chat-card")
    if (chatCard) chatCard.style.height = ""
    this.messagesTarget.style.maxHeight = ""
    this.messagesTarget.style.minHeight = ""
  }

  adjustChatHeight() {
    if (!document.body.classList.contains("advisor-immersive")) return

    const navbar = document.querySelector(".salaries2-navbar")
    const navbarH = navbar ? navbar.offsetHeight : 64
    const chatCard = this.messagesTarget.closest(".advisor-chat-card")
    if (!chatCard) return

    chatCard.style.height = `${window.innerHeight - navbarH}px`
  }

  // Private helpers

  shouldStream() {
    return this.hasStreamUrlValue &&
           this.streamUrlValue &&
           this.manager.stage === "analysis"
  }

  handleResponseData(data) {
    if (!data) return

    if (data.error) {
      renderMessage(this.messagesTarget, "system", data.error)
      if (data.locked || data.rate_limited || data.cost_cap) {
        this.inputAreaTarget.classList.add("hidden")
      }
    } else {
      if (data.goals) {
        renderGoalButtons(this.goalsTarget, data.goals, (goal) => this.selectGoal(goal))
        this.goalsTarget.classList.remove("hidden")
      }

      if (data.summary) {
        renderSummary(this.summaryTarget, data.summary)
        this.summaryTarget.classList.remove("hidden")
        this.inputAreaTarget.classList.add("hidden")
        this.adjustChatHeight()
      }
    }

    this.updateCounters()
    this.scrollToBottom()
  }

  setTypingText(text) {
    if (this.hasTypingTextTarget) {
      updateTypingText(this.typingTextTarget, text)
    }
  }

  detectSessionParams() {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get("new_session")) {
      this._isNewSessionLanding = true
    }

    const type = urlParams.get("session_type")
    const validTypes = [
      "career_checkin", "negotiation_prep", "contractor_evaluation",
      "monthly_checkin", "role_change", "interview_prep", "pre_interview",
      "profile_review", "market_research"
    ]
    if (type && validTypes.includes(type)) {
      this.sessionTypeValue = type
      this._hasSessionTypeParam = true
    }

    const persona = urlParams.get("persona")
    const validPersonas = ["coach", "analyst", "strategist", "mentor"]
    if (persona && validPersonas.includes(persona)) {
      this.selectedPersona = persona
      this.highlightPersonaButton(persona)
    }
  }

  cleanUrl() {
    const url = new URL(window.location.href)
    url.searchParams.delete("new_session")
    url.searchParams.delete("session_type")
    url.searchParams.delete("persona")
    window.history.replaceState({}, "", url.pathname + url.search)
  }

  hidePersonaPicker() {
    if (this.hasPersonaPickerTarget) {
      this.personaPickerTarget.classList.add("hidden")
    }
  }

  highlightPersonaButton(persona) {
    this.element.querySelectorAll(".persona-btn").forEach(btn => {
      if (btn.dataset.persona === persona) {
        btn.classList.add("border-cyan-500", "bg-cyan-50")
        btn.classList.remove("border-gray-200")
      } else if (!btn.disabled) {
        btn.classList.remove("border-cyan-500", "bg-cyan-50")
        btn.classList.add("border-gray-200")
      }
    })
  }

  disableInput() {
    this.sendButtonTarget.disabled = true
    this.inputTarget.disabled = true
  }

  enableInput() {
    this.sendButtonTarget.disabled = false
    this.inputTarget.disabled = false
    this.inputTarget.focus()
  }

  updateCounters() {
    if (this.hasMessageCountTarget) {
      const remaining = this.manager.messagesRemaining
      if (remaining !== undefined && remaining !== null) {
        this.messageCountTarget.textContent = `${remaining} message${remaining === 1 ? "" : "s"} remaining`
      }
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
